/**
 * useMinimapSize Hook
 *
 * Manages minimap resize state. Persists per campaign in localStorage when
 * campaignId is set; otherwise uses legacy global key. Supports external size
 * from flow view visibility (custom views) merged with campaign storage.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  DEFAULT_MAX_WIDTH,
  DEFAULT_MAX_HEIGHT,
  MIN_SIZE,
  MAX_SIZE,
  MIN_MINIMAP_EDGE_PX,
  MINIMAP_SIZE_KEY,
} from '../utils/constants';
import {
  getMinimapSizeForCampaign,
  setMinimapSizeForCampaign,
  saveMinimapSizeGlobal,
} from '../utils/minimapStorage';

/**
 * Legacy global minimap size (no campaignId)
 */
const getStoredMinimapSize = () => {
  try {
    const stored = localStorage.getItem(MINIMAP_SIZE_KEY);
    if (stored) {
      const { maxWidth, maxHeight } = JSON.parse(stored);
      return {
        maxWidth: Math.max(MIN_SIZE, Math.min(MAX_SIZE, maxWidth || DEFAULT_MAX_WIDTH)),
        maxHeight: Math.max(MIN_SIZE, Math.min(MAX_SIZE, maxHeight || DEFAULT_MAX_HEIGHT)),
      };
    }
  } catch (e) {
    console.warn('Failed to load minimap size:', e);
  }
  return { maxWidth: DEFAULT_MAX_WIDTH, maxHeight: DEFAULT_MAX_HEIGHT };
};

function computeDimensions(campaignId, externalSize) {
  if (campaignId) {
    const fromCampaign = getMinimapSizeForCampaign(campaignId);
    if (fromCampaign) return fromCampaign;
  }
  if (externalSize?.maxWidth && externalSize?.maxHeight) {
    return { maxWidth: externalSize.maxWidth, maxHeight: externalSize.maxHeight };
  }
  if (!campaignId) {
    return getStoredMinimapSize();
  }
  return { maxWidth: DEFAULT_MAX_WIDTH, maxHeight: DEFAULT_MAX_HEIGHT };
}

/**
 * Uniform scale of the minimap vs content bounds: min(maxW/bw, maxH/bh).
 */
function getUniformScale(maxWidth, maxHeight, boundsWidth, boundsHeight) {
  if (!boundsWidth || !boundsHeight || !isFinite(boundsWidth) || !isFinite(boundsHeight)) {
    return 0;
  }
  return Math.min(
    (maxWidth || 0) / boundsWidth,
    (maxHeight || 0) / boundsHeight
  );
}

function dimensionsFromUniformScale(k, boundsWidth, boundsHeight) {
  return {
    maxWidth: k * boundsWidth,
    maxHeight: k * boundsHeight,
  };
}

/**
 * @param {React.RefObject} containerRef
 * @param {number} boundsWidth
 * @param {number} boundsHeight
 * @param {Object} [externalSize]
 * @param {Function} [onSizeChange]
 * @param {string} [campaignId]
 */
export const useMinimapSize = (
  containerRef,
  boundsWidth,
  boundsHeight,
  externalSize,
  onSizeChange,
  campaignId
) => {
  const [localDimensions, setLocalDimensions] = useState(() =>
    computeDimensions(campaignId, externalSize)
  );
  const [isResizing, setIsResizing] = useState(null); // 'left' | 'top' | null
  const resizeStartRef = useRef({ x: 0, y: 0, k: 0 });
  const isResizingRef = useRef(false);
  const onSizeChangeRef = useRef(onSizeChange);
  const campaignIdRef = useRef(campaignId);
  onSizeChangeRef.current = onSizeChange;
  campaignIdRef.current = campaignId;

  // Sync from campaign storage / external props when not actively dragging a resize.
  // While resizing we only update local state; localStorage updates on mouseup. If we
  // re-ran this effect on every mousemove (parent gets onMinimapSizeChange each frame),
  // getMinimapSizeForCampaign would still be stale and would snap the minimap back.
  useEffect(() => {
    if (isResizingRef.current) return;
    const next = computeDimensions(campaignId, externalSize);
    setLocalDimensions((prev) => {
      if (prev.maxWidth === next.maxWidth && prev.maxHeight === next.maxHeight) return prev;
      return next;
    });
  }, [campaignId, externalSize?.maxWidth, externalSize?.maxHeight]);

  const setMaxDimensions = useCallback((newDims) => {
    setLocalDimensions(newDims);
  }, []);

  const maxDimensions = localDimensions;

  const handleResizeStart = useCallback((edge, e) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    setIsResizing(edge);
    const bw = boundsWidth;
    const bh = boundsHeight;
    const md = maxDimensions;
    let k0 = getUniformScale(md.maxWidth, md.maxHeight, bw, bh);
    if (!isFinite(k0) || k0 <= 0) {
      k0 = MIN_MINIMAP_EDGE_PX / Math.min(bw, bh);
    }
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      k: k0,
    };
  }, [maxDimensions, boundsWidth, boundsHeight]);

  useEffect(() => {
    if (!isResizing) return;

    const bw = boundsWidth;
    const bh = boundsHeight;
    if (!bw || !bh || !isFinite(bw) || !isFinite(bh)) return;

    const handleResizeMove = (e) => {
      const start = resizeStartRef.current;
      const containerRect = containerRef?.current?.getBoundingClientRect?.();

      let kMax;
      if (containerRect && containerRect.width > 0 && containerRect.height > 0) {
        kMax = Math.min(
          (0.5 * containerRect.width) / bw,
          (0.5 * containerRect.height) / bh
        );
      } else {
        kMax = Math.min(MAX_SIZE / bw, MAX_SIZE / bh);
      }

      const kMinDesired = MIN_MINIMAP_EDGE_PX / Math.min(bw, bh);
      const kMin = kMax < kMinDesired ? 0 : kMinDesired;

      let newK;
      if (isResizing === 'left') {
        const deltaX = start.x - e.clientX;
        newK = start.k + deltaX / bw;
      } else {
        const deltaY = start.y - e.clientY;
        newK = start.k + deltaY / bh;
      }

      newK = Math.min(kMax, Math.max(kMin, newK));
      if (!isFinite(newK)) {
        newK = kMax;
      } else {
        const K_EPS = 1e-12;
        newK = Math.max(K_EPS, Math.min(kMax, newK));
      }
      setMaxDimensions(dimensionsFromUniformScale(newK, bw, bh));
    };

    const handleResizeEnd = () => {
      setLocalDimensions((current) => {
        const cid = campaignIdRef.current;
        if (cid) {
          setMinimapSizeForCampaign(cid, current.maxWidth, current.maxHeight);
        } else {
          saveMinimapSizeGlobal(current.maxWidth, current.maxHeight);
        }
        if (onSizeChangeRef.current) {
          onSizeChangeRef.current({
            maxWidth: current.maxWidth,
            maxHeight: current.maxHeight,
          });
        }
        return current;
      });
      isResizingRef.current = false;
      setIsResizing(null);
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = isResizing === 'left' ? 'ew-resize' : 'ns-resize';
    document.body.style.userSelect = 'none';

    return () => {
      isResizingRef.current = false;
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, boundsWidth, boundsHeight, containerRef, setMaxDimensions]);

  return {
    maxDimensions,
    isResizing,
    handleResizeStart,
  };
};

export default useMinimapSize;
